import { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import type { CameraErrorReason, CameraStatus, UseQRCameraOptions } from './types';
import { logger } from '@/utils/logger';

// torch 속성 타입 확장 (비표준 브라우저 API)
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}

interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean;
}

interface ExtendedMediaTrackSettings extends MediaTrackSettings {
  torch?: boolean;
}

/** QR URL에서 코드 부분을 추출하기 위한 정규식 */
const QR_PATH_REGEX = /\/qr\/((?:ST|SP)-[A-Z0-9]{12})$/;

/** 허용된 QR URL 도메인 목록 */
const ALLOWED_QR_DOMAINS = [
  'menu.olulo.com.mx',
  'menu.dev.olulo.com.mx',
  'menu.staging.olulo.com.mx',
  'localhost',
];

/**
 * QR URL 또는 직접 코드에서 QR 코드를 추출
 * - URL 형식: https://menu.olulo.com.mx/qr/ST-XXXXXXXXXXXX
 * - 직접 코드: ST-XXXXXXXXXXXX 또는 SP-XXXXXXXXXXXX
 */
function extractQrCodeFromUrl(rawValue: string): string | null {
  try {
    const url = new URL(rawValue);
    if (!ALLOWED_QR_DOMAINS.includes(url.hostname)) {
      logger.warn('QR 코드 URL 도메인 불일치:', url.hostname);
      return null;
    }
    const match = url.pathname.match(QR_PATH_REGEX);
    return match ? match[1] : null;
  } catch {
    // URL이 아닌 경우 직접 패턴 매칭
    const directMatch = rawValue.match(/^(ST|SP)-[A-Z0-9]{12}$/);
    return directMatch ? rawValue : null;
  }
}

/**
 * 에러 객체에서 name 을 안전하게 추출
 * (DOMException 이 Error 를 상속하지 않는 구형 환경까지 커버)
 */
function getErrorName(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof (error as { name: unknown }).name === 'string'
  ) {
    return (error as { name: string }).name;
  }
  return '';
}

/**
 * getUserMedia / video.play() 실패를 사용자 조치 가능한 사유로 분류 (#662)
 */
export function classifyCameraError(error: unknown): CameraErrorReason {
  switch (getErrorName(error)) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'permission-denied';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'no-device';
    case 'NotReadableError':
    case 'AbortError':
      return 'in-use';
    default:
      return 'unknown';
  }
}

/**
 * Permissions API 로 카메라 권한 상태를 사전 조회 (fail-soft)
 * - 미지원/조회 실패 시 null 반환 → 기존 getUserMedia 흐름으로 진행
 */
async function queryCameraPermission(): Promise<PermissionState | null> {
  try {
    const status = await navigator.permissions?.query?.({
      name: 'camera' as PermissionName,
    });
    return status?.state ?? null;
  } catch {
    // Permissions API 미지원(Safari 구버전 등)/'camera' name 미지원 — 무시하고 기존 흐름
    return null;
  }
}

export function useQRCamera(options: UseQRCameraOptions = {}) {
  const { onScanResult } = options;

  const [flashOn, setFlashOn] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('idle');
  const [cameraError, setCameraError] = useState<CameraErrorReason | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const scanningRef = useRef(false);
  const cameraRequestRef = useRef(0);

  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStream(null);
    }
    setFlashOn(false);
  }, []);

  const cleanup = useCallback(() => {
    cameraRequestRef.current += 1;
    stopCameraStream();
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    scanningRef.current = false;
  }, [stopCameraStream]);

  const startScanLoop = useCallback(() => {
    if (!videoRef.current || scanningRef.current) return;
    scanningRef.current = true;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      scanningRef.current = false;
      logger.error('QR 스캔: Canvas 2D 컨텍스트를 생성할 수 없습니다');
      return;
    }

    const scan = () => {
      const video = videoRef.current;
      if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
        animationFrameRef.current = requestAnimationFrame(scan);
        return;
      }

      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        if (canvas.width === 0 || canvas.height === 0) {
          animationFrameRef.current = requestAnimationFrame(scan);
          return;
        }
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code?.data) {
          const qrCode = extractQrCodeFromUrl(code.data);
          if (qrCode && onScanResult) {
            scanningRef.current = false;
            onScanResult(qrCode);
            return;
          }
        }
      } catch (error) {
        logger.error('QR 스캔 루프 오류:', error);
      }

      animationFrameRef.current = requestAnimationFrame(scan);
    };

    animationFrameRef.current = requestAnimationFrame(scan);
  }, [onScanResult]);

  /**
   * 카메라 안내 상태 전이 (#662)
   * - 권한/기기 상태는 안내로 처리하고 예상하지 못한 실패는 Sentry에 기록
   * - 최신 요청일 때만 error 상태로 전이 (stale 요청 무시)
   */
  const failCamera = useCallback(
    (requestId: number, reason: CameraErrorReason, error: unknown) => {
      const errorName = getErrorName(error);
      if (reason === 'permission-denied' || reason === 'no-device' || reason === 'in-use') {
        // Known browser/device conditions already have recovery instructions.
        logger.info('카메라 사용 안내:', { reason, errorName });
      } else {
        const errorMessage =
          typeof error === 'object' && error !== null && 'message' in error
            ? String((error as { message: unknown }).message)
            : String(error);
        // DOMException 이 Error 를 상속하지 않는 환경에서도 Sentry 가 예외로 수집하도록 보정
        const capturable =
          error instanceof Error
            ? error
            : new Error(`${errorName || 'CameraError'}: ${errorMessage}`);
        logger.error('카메라 시작 실패:', capturable, {
          reason,
          errorName,
          errorMessage,
        });
      }
      if (cameraRequestRef.current === requestId) {
        setCameraError(reason);
        setCameraStatus('error');
      }
    },
    [],
  );

  const startCamera = useCallback(async () => {
    const requestId = cameraRequestRef.current + 1;
    cameraRequestRef.current = requestId;
    setCameraStatus('starting');
    setCameraError(null);

    // in-app webview 등 mediaDevices 자체 부재 — 프롬프트 없이 즉시 실패하는 (B) 후보 (#662)
    if (!navigator.mediaDevices?.getUserMedia) {
      failCamera(requestId, 'unsupported', new Error('mediaDevices.getUserMedia is not available'));
      return;
    }

    // 이전 거부 기억 (A) 후보: 프롬프트 시도 전에 권한 상태 사전 조회 (fail-soft) (#662)
    const permissionState = await queryCameraPermission();
    if (cameraRequestRef.current !== requestId) return;
    if (permissionState === 'denied') {
      failCamera(
        requestId,
        'permission-denied',
        new Error('camera permission denied (Permissions API pre-check)'),
      );
      return;
    }

    try {
      stopCameraStream();

      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 640 },
            height: { ideal: 640 },
          },
          audio: false,
        });
      } catch (error) {
        const name = getErrorName(error);
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          throw error;
        }
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        logger.info('후면 카메라 대신 기본 카메라를 사용합니다.');
      }

      if (cameraRequestRef.current !== requestId) {
        mediaStream.getTracks().forEach(track => track.stop());
        return;
      }

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setCameraStatus('active');
    } catch (error) {
      failCamera(requestId, classifyCameraError(error), error);
    }
  }, [stopCameraStream, failCamera]);

  const handleFlashToggle = useCallback(async () => {
    if (!stream) {
      logger.warn('No camera stream available');
      return;
    }

    try {
      const track = stream.getVideoTracks()[0];
      if (!track) {
        logger.warn('No video track available');
        return;
      }

      const capabilities = track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
      if (!capabilities?.torch) {
        logger.info('Torch not supported on this device');
        return;
      }

      const newFlashState = !flashOn;

      await track.applyConstraints({
        advanced: [{ torch: newFlashState } as ExtendedMediaTrackConstraintSet],
      });

      setFlashOn(newFlashState);
      logger.info(`Flash ${newFlashState ? 'ON' : 'OFF'}`);

    } catch (error) {
      logger.error('Failed to toggle flash:', error);
      try {
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings() as ExtendedMediaTrackSettings;

        if (settings.torch !== undefined) {
          await track.applyConstraints({
            torch: !flashOn,
          } as MediaTrackConstraints);
          setFlashOn(!flashOn);
        }
      } catch {
        // 의도적으로 에러 무시: 플래시 컨트롤이 지원되지 않는 디바이스에서는 무시
        logger.warn('Flash control not available on this device');
      }
    }
  }, [stream, flashOn]);

  useEffect(() => {
    startCamera();
    return () => {
      cleanup();
    };
  }, [startCamera, cleanup]);

  useEffect(() => {
    const video = videoRef.current;
    if (!stream || !video) return;

    video.srcObject = stream;
    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch((error: unknown) => {
        // AbortError: srcObject 교체/cleanup 에 의한 정상 중단 — 에러 상태로 전파하지 않음
        // stale stream: 이미 다른 스트림으로 교체됨 — 무시
        if (getErrorName(error) === 'AbortError' || streamRef.current !== stream) {
          logger.warn('카메라 미리보기 자동 재생 실패:', error);
          return;
        }
        // play() 실패도 동일 분류로 전파 (#662) — 예: 비제스처 자동재생 거부(NotAllowedError)
        stopCameraStream();
        failCamera(cameraRequestRef.current, classifyCameraError(error), error);
      });
    }

    return () => {
      if (video.srcObject === stream) {
        video.srcObject = null;
      }
    };
  }, [stream, stopCameraStream, failCamera]);

  // 카메라 활성화 + onScanResult 콜백이 있을 때 스캔 루프 시작
  useEffect(() => {
    if (cameraStatus === 'active' && onScanResult) {
      startScanLoop();
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      scanningRef.current = false;
    };
  }, [cameraStatus, onScanResult, startScanLoop]);

  return {
    flashOn,
    cameraStatus,
    cameraError,
    videoRef,
    handleFlashToggle,
    startCamera,
  };
}
